import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ProgressNotes.module.scss";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import Button from "../../../common/Button/Button";
import { useAppSelector } from "../../../../redux/hooks";
import sortIconLight from "../../../../assets/images/LightTheme/up_down.svg";
import sortIconDark from "../../../../assets/images/DarkTheme/up_down_dark.svg";
import FormatterBar from "../../../common/FormatterBar/FormatterBar";
import {
  useGetUsersListByNativeIDsQuery,
} from "../../../../redux/services/progressNotesAPI";
import { RootState } from "../../../../redux/store";
import HTMLtoText from "../../../common/HTMLToText/HTMLToText";
import { formatDate } from "../../../../utils/dateFormatter";
import {
  NOTES_SORT_LIST,
  countryCodeMappings,
} from "../../../../constants/dataConstants";
import ABOBackgroundLoader from "../../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";
import ErrorInPageScreen from "../../../common/ErrorInPageScreen/ErrorInPageScreen";
import { isValidResponse } from "../../../../utils/validateAPIResponse";
import {
  getFullNameOfUserByNativeID,
  getUserNativeIDsArray,
} from "../../../../utils/progressNotesUtil";
import { UserDetails } from "../../../../types/types";
import useClickOutside from "../../../CustomHooks/useClickOutside";
import { convertUrlsToLinks, isOwner } from "../../../../utils/common";
import { useProfileParams } from "../../../../utils/common";
import YearDropdown from "../../../common/YearDropdown/YearDropdown";
import FlatButtonAction from "../../../common/FlatButtonAction/FlatButtonAction";
import GenericIcon from "../../../common/GenericIcon/GenericIcon";

interface ProgressNote {
  aboNoteId: number;
  note: string;
  title: string;
  performanceYear: number;
  audit: {
    createdDate: string;
    createdBy: string;
    updatedDate: string;
    updatedBy: string;
  };
}
interface ProgressNotesProps {
  api: {
    useGetNotes: Function;
    useCreateNote: Function;
    useEditNote: Function;
    useDeleteNote: Function;
  };
  type?: string;
  goalId?: number;
  isHistoryGoalData?: boolean;
}

type FilterDataType = {
  id: string;
  name: string;
};

const ProgressNotes: React.FC<ProgressNotesProps> = ({ api, type, goalId, isHistoryGoalData }) => {
  const { useGetNotes, useCreateNote, useEditNote, useDeleteNote } = api;
  const { t } = useTranslation();
  const [progressNotes, setProgressNotes] = useState<ProgressNote[]>([]);
  const [userNativeIDs, setUserNativeIDs] = useState<string[]>([]);
  const [updatedNativeIDs, setUpdatedNativeIDs] = useState<string[]>([]);
  const [usersListByNativeId, setUsersListByNativeId] = useState<
    UserDetails[] | null
  >([]);
  const [textareaValue, setTextareaValue] = useState<string>("");
  const [isEditable, setIsEditable] = useState(false);
  const [isNoteEditable, setNoteEditable] = useState(false);
  const [saveEnable, setSaveEnable] = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);
  const [sortByNewest, setSortByNewest] = useState(true);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const selectedYear = useAppSelector(
    (state: RootState) => state.selectedYear.selectedYear
  );
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const [isAPIError, setIsAPIError] = useState<boolean>(false);
  const [selectedSort, setSelectedSort] = useState<FilterDataType>(
    NOTES_SORT_LIST?.[0]
  );
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);

  const sortIcon = currentTheme === "light" ? sortIconLight : sortIconDark;
  const focusHandler = () => {
    setSaveEnable(false);
    setIsEditable(true);
    setNoteEditable(false);
  };
  const handleClickCancel = () => {
    setIsEditable(false);
    setNoteEditable(false);
  };
  const { affiliateCode, aboNumber } = useProfileParams();

  //API call to get list of all notes
  const {
    isFetching: isNotesLoading,
    isError: isErrorInFetchNotes,
    data: notesData,
    refetch: notesRefetch,
  } = useGetNotes(
    type === "growthPlan"
      ? { affiliateCode, aboNumber, goalId: goalId! }
      : { affiliateCode, aboNumber, year: selectedYear ?? 0 },
    { skip: isAPIError || (type === "growthPlan" && !goalId) }
  ); 

  const { data: usersList } = useGetUsersListByNativeIDsQuery(
    {
      nativeIDs: userNativeIDs,
    },
    {
      skip: isEmpty(userNativeIDs) || userNativeIDs === updatedNativeIDs,
    }
  );

  useEffect(() => {
    setUsersListByNativeId(usersList?.body);
    if (!isEmpty(usersList)) {
      setUpdatedNativeIDs(userNativeIDs);
    }
  }, [usersList]);

  const sortDataByOrder = (sortByNewest: boolean) => {
    if (sortByNewest) {
      return [...notesData.body]
        .filter((note) => note.audit && note.audit.updatedDate)
        .sort(
          (a, b) =>
            new Date(b.audit.updatedDate).getTime() -
            new Date(a.audit.updatedDate).getTime()
        );
    } else {
      return [...notesData.body]
        .filter((note) => note.audit && note.audit.updatedDate)
        .sort(
          (a, b) =>
            new Date(a.audit.updatedDate).getTime() -
            new Date(b.audit.updatedDate).getTime()
        );
    }
  };

  const handleEditTag = (updatedDate: string, createdDate: string) => {
    if (
      new Date(updatedDate)?.toLocaleString() ===
      new Date(createdDate)?.toLocaleString()
    ) {
      return "";
    } else {
      return t("edited");
    }
  };

  const handleAPIError = () => {
    setIsAPIError(true);
    setProgressNotes([]);
  };

  useEffect(() => {
    if (isNotesLoading) return;
    if (notesData && Array.isArray(notesData.body)) {
      if (isValidResponse(notesData?.statusCode)) {
        const sortedNotes = sortDataByOrder(
          selectedSort?.id === "newest_first"
        );
        setProgressNotes(sortedNotes);
        setUserNativeIDs(getUserNativeIDsArray(sortedNotes));
      } else {
        handleAPIError();
        setProgressNotes([]);
      }
    } else if (notesData?.statusCode === 404) {
      setProgressNotes([]);
    }
  }, [notesData, isNotesLoading]);

  const handleEdit = (note: any) => {
    setSaveEnable(false);
    setNoteEditable(true);
    setIsEditable(false);
    setTextareaValue(note.note);
    setEditNoteId(note.aboNoteId);
  };

  const [
    deleteNote,
    { isLoading: isLoadingDelete, isError: isErrorInDelete },
  ] = useDeleteNote();

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteNote({ id: noteId });
      await notesRefetch();
    } catch (error) {
      handleAPIError();
    }
  };

  //API call to create new note.
  const [
    createNotes,
    { isLoading: isNotesCreating, isError: isErrorInCreateNote },
  ] = useCreateNote();

  const [editNotes, { isLoading: isNotesEditing, isError: isErrorInEditNote }] =
    useEditNote();

  useEffect(() => {
    if (
      isErrorInFetchNotes ||
      isErrorInCreateNote ||
      isErrorInEditNote ||
      isErrorInDelete
    )
      handleAPIError();
  }, [
    isErrorInFetchNotes,
    isErrorInCreateNote,
    isErrorInEditNote,
    isErrorInDelete,
  ]);

  const handleClickSave = async () => {
    const payload = {
      performanceYear: type === "growthPlan" ? 0 : selectedYear ?? 0,
      title: "",
      note: textareaValue,
      metadata: type === "growthPlan" ? { goalId: goalId! } : undefined,
    };

    if (editNoteId) {
      const editNotesResponse: any = await editNotes({
        affiliateCode,
        aboNumber,
        payload: { ...payload, aboNoteId: editNoteId },
      });
      setEditNoteId(null);
      if (!isValidResponse(editNotesResponse?.data?.statusCode)) {
        handleAPIError();
      }
    } else {
      const response: any = await createNotes({
        affiliateCode,
        aboNumber,
        payload: payload,
      });

      if (!isValidResponse(response?.data?.statusCode)) {
        handleAPIError();
      }
    }

    setIsEditable(false);
    setNoteEditable(false);
  };

  const textAreaChangeHandler = (editorData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorData, "text/html");
    const rowNote = (doc.body.textContent || "").trim();
    const hasImage = !!doc.querySelector("img");
    setSaveEnable(hasImage || rowNote.length > 0);
    editorData === "<p><br></p>"
      ? setTextareaValue("")
      : setTextareaValue(editorData);
  };

  const handleClickSortIcon = () => {
    setIsOpenPopup(!isOpenPopup);
  };

  const handleChangeSort = (sortItem: FilterDataType) => {
    if (sortItem?.id !== selectedSort?.id) {
      const sortedNotes = [...progressNotes].reverse();
      setProgressNotes(sortedNotes);
    }
    setSelectedSort(sortItem);
  };
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpenPopup(false));

  return (
    <section
      className={`notes_container ${styles.notes_container} ${isAPIError ? styles.full_width : ""}`}
    >
      {isNotesCreating ||
      isNotesLoading ||
      isNotesEditing ||
      isLoadingDelete ? (
        <ABOBackgroundLoader num={5} heading={t("notes")} />
      ) : isAPIError ? (
        <>
          <div className={styles.heading_area}>
            <div
              className={styles.title}
              data-testid="progress_notes_bg_heading_testID"
            >
              {t("notes")}
            </div>
          </div>
          <div data-testid="error_testID">
            <ErrorInPageScreen
              handleClickTryAgain={() => {
                setIsAPIError(false);
              }}
            />
          </div>
        </>
      ) : (
        <>
          {type !== "growthPlan" && (
            <>
              {" "}
              <div className={styles.year_dropdown}>
                <YearDropdown />
              </div>
            </>
          )}
          <div className={styles.heading_area}>
            <div
              className={`${styles.title} ${type === "growthPlan" ? styles.growth_plan_title : ""}`}
              data-testid="progress_notes_bg_heading_testID"
            >
              {t("notes")}
            </div>
            <div className={styles.sort_section} onClick={handleClickSortIcon}>
              <div className={styles.sort_label}>
                {selectedSort?.id === "newest_first"
                  ? t("newest_first")
                  : t("oldest_first")}
              </div>
              <img src={sortIcon} alt="" className={styles.sort_icon} />
              {isOpenPopup && (
                <div
                  className={`dropdown_container ${styles.dropdown_container}`}
                >
                  <div
                    ref={dropdownRef}
                    className={`dropdown_menu ${styles.dropdown_menu}`}
                  >
                    {map(NOTES_SORT_LIST, (filterItem) => (
                      <div
                        key={filterItem.id}
                        className={`${selectedSort?.id === filterItem?.id ? "dropdown_selected_item" : ""} ${styles.dropdown_item}`}
                        onClick={() => handleChangeSort(filterItem)}
                      >
                        {t(`${filterItem.id}`)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.input_wrapper}>
            {isEditable ? (
              <>
                <div className={styles.formatter_wrapper}>
                  <FormatterBar
                    id="notes"
                    testId="notes_testID"
                    editorData={""}
                    handleDataChange={textAreaChangeHandler}
                    section={"notes"}
                    placeholder={t("add_comment")}
                    isContentEditable={true}
                    maxCharacter={20000}
                  />
                </div>
                <div className={styles.btn_wrapper}>
                  <Button
                    id="cancelButton"
                    onClick={handleClickCancel}
                    label={t("btnCancel")}
                    style="unfilled"
                  />
                  <Button
                    id="saveButton"
                    onClick={handleClickSave}
                    label={t("btnSave")}
                    style="filled"
                    isDisabled={!saveEnable || !textareaValue}
                  />
                </div>
              </>
            ) : (
            !isHistoryGoalData && (
                <input
                type="text"
                className={`input_text_area ${styles.input_text_area}`}
                placeholder={t("add_comment")}
                value={t("add_comment")}
                onClick={focusHandler}
                readOnly
                data-testid="textarea_testID"
              />
            )
            )}
          </div>
          {map(
            showAllNotes || type !== "growthPlan"
              ? progressNotes
              : progressNotes.slice(0, 3),
            (note) => (
              <div
                key={note.aboNoteId}
                className={`progress_note ${styles.progress_note}`}
              >
                <div className={`left_wrapper ${styles.left_wrapper}`}>
                    <GenericIcon isLight={currentTheme === 'light'}/>
                </div>
                <div className={`right_wrapper ${styles.right_wrapper}`}>
                  <div className={styles.user_info}>
                    <div className={styles.user_name}>
                      {getFullNameOfUserByNativeID(
                        note.audit.createdBy,
                        usersListByNativeId
                      )}
                    </div>
                    <div className={`last_modified ${styles.last_modified}`}>
                      {/* {new Date(note.audit.updatedDate).toLocaleString()} */}
                      {formatDate(
                        note.audit.updatedDate,
                        countryCodeMappings[userAffiliateCode],
                        "dateTime"
                      )}
                      <span className={styles.edited_tag}>
                        {handleEditTag(
                          note.audit.updatedDate,
                          note.audit.createdDate
                        )}
                      </span>
                    </div>
                  </div>
                  <div className={styles.input_wrapper}>
                    {isNoteEditable && editNoteId === note.aboNoteId ? (
                      <>
                        <div
                          className={styles.formatter_wrapper}
                          data-testid="editor_testID"
                        >
                          <FormatterBar
                            id="notes"
                            testId="notes_testID"
                            editorData={textareaValue}
                            handleDataChange={textAreaChangeHandler}
                            section={"notes"}
                            placeholder={t("add_comment")}
                            isContentEditable={true}
                            maxCharacter={20000}
                          />
                        </div>
                        <div className={styles.btn_wrapper}>
                          <Button
                            id="cancelButton"
                            onClick={handleClickCancel}
                            label={t("btnCancel")}
                            style="unfilled"
                          />
                          <Button
                            id="saveButton"
                            onClick={handleClickSave}
                            label={t("btnSave")}
                            style="filled"
                            isDisabled={!saveEnable || !textareaValue}
                          />
                        </div>
                      </>
                    ) : (
                      <div>
                        <div
                          className={`note_content ${styles.note_content}`}
                          data-testid="all_notes_testID"
                        >
                          <p>
                            {(() => {
                              const parser = new DOMParser();
                              const doc = parser.parseFromString(note?.note || '', 'text/html');
                              const hasImage = !!doc.querySelector('img');
                              if (!hasImage && note?.note?.length > 1000) {
                                return (
                                  <HTMLtoText
                                    htmlContent={convertUrlsToLinks(
                                      note?.note || '',
                                      currentTheme
                                    )}
                                    maxTextLength={1000}
                                  />
                                );
                              }
                              return (
                                <HTMLtoText
                                  htmlContent={convertUrlsToLinks(
                                    note?.note,
                                    currentTheme
                                  )}
                                />
                              );
                            })()}
                          </p>
                        </div>
                        {!isHistoryGoalData && (
                           <div className={`action_btns ${styles.action_btns}`}>
                           <FlatButtonAction
                             label={t("edit")}
                             onClick={() => handleEdit(note)}
                             className={styles.action_edit}
                             testId="edit_testID"
                             disabled={!isOwner(note?.audit?.createdBy)}
                             tooltipDescription={'access_restricted_to_creator'}
                           />
                           <FlatButtonAction
                             label={t("delete")}
                             onClick={() => handleDeleteNote(note.aboNoteId)}
                             className={styles.action_delete}
                             testId="delete_testID"
                             disabled={!isOwner(note?.audit?.createdBy)}
                             tooltipDescription={'access_restricted_to_creator'}
                           />
                         </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
          {type === "growthPlan" && progressNotes.length > 3 && (
            <div
              className={`view_more_notes ${styles.view_more_notes}`}
              onClick={() => setShowAllNotes((prev) => !prev)}
            >
              {showAllNotes ? t("view_less") : t("view_more")}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ProgressNotes;
